import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@caspeco/casper-ui-library.components.box";
import { Image } from "@caspeco/casper-ui-library.components.image";
import { Text } from "@caspeco/casper-ui-library.components.text";
import { SkeletonArticleDetails } from "components/skeletons/SkeletonArticleDetails";
import {
	ThemeColorVariable,
	ThemeFontSizeVariable,
	ThemeFontVariable,
	ThemeRadiusVariable,
	ThemeSpaceVariable,
} from "@caspeco/casper-ui-library.base-ui.theme";
import { Heading } from "@caspeco/casper-ui-library.components.heading";
import { useToast } from "@caspeco/casper-ui-library.components.toast";
import { Modal, ModalBody, ModalCloseButton } from "@caspeco/casper-ui-library.components.modal";
import { Stack } from "@caspeco/casper-ui-library.components.stack";
import { usePickSearchParams } from "hooks/usePickSearchParams";
import { useCartAPI, useCartStore } from "store/cartStore";
import { usePlaceStore } from "store/placeStore";
import { useMenuStore } from "store/menuStore";
import { useArticleStore } from "store/articleStore";
import { getMenuSectionAlert } from "helpers/menu-section-opening-hours-helper/menu-section-opening-hours-helper";

import { logError, logInfo } from "helpers/log-helper/log-helper";

import { Section } from "types/section";
import { Article } from "types/article";
import { CartRow, CartRowDraft } from "types/cart";
import { LogPrefix } from "types/logging";
import { AspectRatio } from "types/ui";
import { BSTLApiError } from "types/api-error";
import { PriceIncludingVat, PriceType } from "types/price";
import { getPriceTypeFromDeliveryType } from "helpers/price-helper/price-helper";
import { DeliveryType } from "types/deliveryOption";
import { AspectRatioWrapper } from "components/aspect-ratio-wrapper/AspectRatioWrapper";
import Price from "components/price/Price";
import NotFound from "components/not-found/NotFound";
import { shouldLogError } from "helpers/error-code-helper/bstl-api-error-helper";
import {
	getNextOptionsSelection,
	getAlertBasedOnAddableToCartFailReasons,
	getSectionForArticle,
	initializeCartRow,
	getTotalPrice,
} from "./ArticleModal.helpers";
import { ArticleModalOptions } from "./subcomponents/ArticleModalOptions";
import { ArticleModalExtras } from "./subcomponents/ArticleModalExtras";
import { ArticleModalFooter } from "./subcomponents/ArticleModalFooter";
import { AllergensInfo } from "./subcomponents/AllergensInfo";

interface IArticleDetails {
	/** Article object to display. */
	article: Article;
	/** If Article Details Modal is toggled from cart row click or from sign in flow,
	 * the article to be shown should match the cart row representation provided here. */
	defaultValues: CartRow | CartRowDraft | undefined;
	isOpenedFromCart?: boolean;
	onClose: () => void;
	/** Called when upsell for article is triggered */
	onOpenUpsell?: () => void;
}

function ArticleDetails({
	article,
	defaultValues,
	onClose,
	isOpenedFromCart,
	onOpenUpsell,
}: IArticleDetails) {
	const { showToast } = useToast();
	const { cart } = useCartStore();
	const { addCartRow, createCart, updateCartRow, deleteCartRow } = useCartAPI();
	const { menu } = useMenuStore();
	const { place } = usePlaceStore();
	const {
		description,
		name,
		inHousePriceIncludingVat,
		takeAwayPriceIncludingVat,
		imageMap,
		addableToCart,
		addableToCartFailReasons,
	} = article;
	const { deliveryOptionType, deliveryOptionId } = usePickSearchParams([
		"deliveryOptionType",
		"deliveryOptionId",
	]);
	const priceType = getPriceTypeFromDeliveryType(deliveryOptionType as DeliveryType);

	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [totalPrice, setTotalPrice] = useState<PriceIncludingVat>(() =>
		getPriceTypeFromDeliveryType(deliveryOptionType as DeliveryType) === PriceType.TAKEAWAY
			? takeAwayPriceIncludingVat
			: inHousePriceIncludingVat,
	);

	const [cartRow, setCartRow] = useState<CartRowDraft>(() => initializeCartRow(article, defaultValues));

	const initCartRow = useMemo(() => {
		return initializeCartRow(article, defaultValues);
	}, [article, defaultValues]);

	const hasChanges = useMemo(() => {
		const hasCartChanges =
			initCartRow.quantity !== cartRow.quantity ||
			initCartRow.options.some((option) => !cartRow.options.includes(option)) ||
			cartRow.options.some((option) => !initCartRow.options.includes(option)) ||
			initCartRow.extras.some((extras) => !cartRow.extras.includes(extras)) ||
			cartRow.extras.some((extras) => !initCartRow.extras.includes(extras));

		return hasCartChanges;
	}, [
		cartRow.extras,
		cartRow.options,
		cartRow.quantity,
		initCartRow.extras,
		initCartRow.options,
		initCartRow.quantity,
	]);

	const { t } = useTranslation();
	const parentSection: Section | undefined = menu ? getSectionForArticle(menu, article.id) : undefined;

	const hasAddons = article.extras.length > 0 || article.options.length > 0;

	const getCalculatedTotalPrice = useCallback(() => {
		const newTotalPriceInCents = getTotalPrice(priceType, article, cartRow);
		const newTotalPrice = new PriceIncludingVat({
			cents: newTotalPriceInCents,
			currency: totalPrice.currency,
		});
		return newTotalPrice;
	}, [priceType, article, cartRow, totalPrice.currency]);

	useEffect(() => {
		// Re-calculate price total everytime quantity, options, extras or delivery type changes
		const newPrice = getCalculatedTotalPrice();
		setTotalPrice(newPrice);
	}, [cartRow.quantity, cartRow.options, cartRow.extras, deliveryOptionType, getCalculatedTotalPrice]);

	const onChangeArticleQuantity = (quantity: number) => {
		setCartRow({ ...cartRow, quantity });
	};

	const onAddToCart = async () => {
		try {
			setIsSubmitting(true);
			if (!menu) throw new Error();

			// If no cart exists yet, this is were we create one
			if (!cart) {
				if (!deliveryOptionType) {
					throw new Error("Missing Delivery Type in URL");
				}
				const newCart = await createCart(menu.id, deliveryOptionType, deliveryOptionId);
				await addCartRow(newCart.cartId, cartRow);
			} else {
				// Add items to existing cart
				await addCartRow(cart.cartId, cartRow);
			}
			logInfo(
				LogPrefix.Cart,
				`Article added to Cart, Name: ${article.name}, Id: ${article.id}, Quantity: ${cartRow.quantity}`,
			);
			setIsSubmitting(false);
			onClose();
			// Trigger upsell if any, after close
			if (article.hasUpsellGroups && onOpenUpsell) {
				onOpenUpsell();
			}
		} catch (error) {
			if (shouldLogError(error, ["ARTICLE_OUT_OF_STOCK"])) {
				logError(
					LogPrefix.Cart,
					error,
					`Could not add Article to Cart, Name: ${article.name}, Id: ${article.id}`,
				);
			}

			showToast({
				type: "error",
				description: error instanceof BSTLApiError ? error.translatedMessage : t("error_add_to_cart"),
			});

			setIsSubmitting(false);
			onClose();
		}
	};

	const onUpdate = async () => {
		try {
			setIsSubmitting(true);

			if (!cart || !isOpenedFromCart || !defaultValues) {
				throw new Error("Attempted to update Article from Cart without values to delete");
			}
			if (defaultValues instanceof CartRowDraft) {
				throw new Error("Attempted to update Article not yet added to Cart (draft), should not be possible");
			}

			await updateCartRow(cart.cartId, defaultValues.id, cartRow);
			setIsSubmitting(false);
			logInfo(
				LogPrefix.Cart,
				`Updated existing Article in Cart, Name: ${article.name}, Id: ${article.id}, Quantity: ${defaultValues.quantity}`,
			);
			onClose();
		} catch (error) {
			if (shouldLogError(error, ["ARTICLE_OUT_OF_STOCK"])) {
				logError(
					LogPrefix.Cart,
					error,
					`Could not update Article in Cart, Name: ${article.name}, Id: ${article.id}`,
				);
			}

			const message: string =
				error instanceof BSTLApiError ? error.translatedMessage : t("error_update_cart");

			showToast({
				type: "error",
				description: message,
			});

			setIsSubmitting(false);
			onClose();
		}
	};

	const onDelete = async () => {
		try {
			setIsSubmitting(true);

			if (!cart || !isOpenedFromCart || !defaultValues) {
				throw new Error("Attempted to delete Article from Cart without values to delete");
			}
			if (defaultValues instanceof CartRowDraft) {
				throw new Error("Attempted to delete Article not yet added to Cart (draft), should not be possible");
			}

			await deleteCartRow(cart.cartId, defaultValues.id);
			setIsSubmitting(false);
			logInfo(LogPrefix.Cart, `Deleted Article from Cart, Name: ${article.name}, Id: ${article.id}`);
			onClose();
		} catch {
			showToast({
				type: "error",
				description: t("error_delete_article"),
			});
			setIsSubmitting(false);
			onClose();
		}
	};

	const onChangeExtras = (nextValue: string, checked: boolean): void => {
		const newCartRowExtras = cartRow.extras;
		const indexOfExtra = newCartRowExtras.indexOf(nextValue);
		if (indexOfExtra >= 0 && !checked) {
			// Extra was unchecked, remove it
			newCartRowExtras.splice(indexOfExtra, 1);
		} else {
			// Some not previously selected extra was checked, add it
			newCartRowExtras.push(nextValue);
		}
		setCartRow({ ...cartRow, extras: [...newCartRowExtras] });
	};

	const onChangeOptions = (nextValue: string): void => {
		const updatedOptions = getNextOptionsSelection(nextValue, article, cartRow);
		setCartRow({ ...cartRow, options: [...updatedOptions] });
	};

	// True if either is closed
	const placeOrMenuOrSectionIsClosed: boolean = Boolean(
		parentSection?.open === false || menu?.open === false || place?.open === false,
	);

	// Closed and no pre order is available, article cannot be purchased.
	// Consider isOpenedFromCart an exception to rule as as
	// section might have closed while shopping (see usage in ArticleDetailsFooter)
	// OR not adddable to cart with reason detected from backend exists
	const isNotAvailableForPurchase =
		(!isOpenedFromCart && placeOrMenuOrSectionIsClosed && place?.preOrderEnabled === false) || !addableToCart;

	const alert = !addableToCart
		? getAlertBasedOnAddableToCartFailReasons(addableToCartFailReasons)
		: getMenuSectionAlert(place, menu, parentSection);

	return (
		<>
			<ModalBody>
				{imageMap.hasImage(AspectRatio.SixteenByNine) && (
					<Box w="100%">
						<Image
							w="100%"
							h="auto"
							alt={description}
							src={imageMap.withSizeQuery(AspectRatio.SixteenByNine, "l")}
							srcSet={imageMap.getScrSet(AspectRatio.SixteenByNine, "l", "xl")}
							borderTopRadius={ThemeRadiusVariable.Medium}
							fallback={
								<AspectRatioWrapper $aspectratio={AspectRatio.SixteenByNine}>
									<Box bg={ThemeColorVariable.SurfaceDisabled} w="100%" />
								</AspectRatioWrapper>
							}
						/>
					</Box>
				)}
				<Box p={ThemeSpaceVariable.Medium}>
					<Stack spacing={ThemeSpaceVariable.Small}>
						<Heading
							as="h2"
							fontWeight="medium"
							fontFamily={ThemeFontVariable.SubHeader}
							fontSize={ThemeFontSizeVariable.XLarge}
							color={ThemeColorVariable.OnSurfaceHeader}
							maxW="calc(100% - 56px)"
						>
							{name}
						</Heading>
						{description && (
							<Text
								fontSize={ThemeFontSizeVariable.Large}
								color={ThemeColorVariable.OnSurface}
								whiteSpace="break-spaces"
								mt={ThemeSpaceVariable.Medium}
							>
								{description}
							</Text>
						)}
					</Stack>
					<AllergensInfo />
					<Price
						mt={ThemeSpaceVariable.Small}
						fontSize={ThemeFontSizeVariable.XLarge}
						fontFamily={ThemeFontVariable.SubHeader}
						inHousePrice={article.inHousePriceIncludingVat}
						takeAwayPrice={article.takeAwayPriceIncludingVat}
						color={ThemeColorVariable.Secondary}
					/>
					{hasAddons && (
						<Stack spacing={ThemeSpaceVariable.Large} mt={ThemeSpaceVariable.Large}>
							{article.options.map((option) => (
								<ArticleModalOptions
									key={option.id}
									option={option}
									isNotAvailableForPurchase={isNotAvailableForPurchase}
									selectedOptions={cartRow.options}
									onChange={onChangeOptions}
								/>
							))}
							{article.extras.map((extra) => (
								<ArticleModalExtras
									key={extra.id}
									extra={extra}
									isNotAvailableForPurchase={isNotAvailableForPurchase}
									selectedExtras={cartRow.extras}
									onChange={onChangeExtras}
								/>
							))}
						</Stack>
					)}
				</Box>
			</ModalBody>
			<ArticleModalFooter
				totalPrice={totalPrice}
				quantity={cartRow.quantity}
				onAdd={onAddToCart}
				onDelete={onDelete}
				onUpdate={onUpdate}
				isSubmitting={isSubmitting}
				isOpenedFromCart={isOpenedFromCart}
				isNotAvailableForPurchase={isNotAvailableForPurchase}
				alert={alert}
				onChange={onChangeArticleQuantity}
				minQuantity={isOpenedFromCart ? 0 : 1}
				hasChanges={hasChanges}
			/>
		</>
	);
}

interface IArticleModal {
	defaultValues?: CartRow | undefined;
	onClose: () => void;
	isOpen: boolean;
	isOpenedFromCart?: boolean;
	onOpenUpsell?: () => void;
}

function ArticleModal({ defaultValues, isOpen, isOpenedFromCart, onClose, onOpenUpsell }: IArticleModal) {
	const { article, isLoadingArticle, hasLoadedArticle } = useArticleStore();
	let content: JSX.Element = <></>;

	if (isLoadingArticle) {
		content = <SkeletonArticleDetails />;
	}

	if (hasLoadedArticle && article) {
		content = (
			<ArticleDetails
				article={article}
				onClose={onClose}
				isOpenedFromCart={isOpenedFromCart}
				onOpenUpsell={onOpenUpsell}
				defaultValues={defaultValues}
			/>
		);
	}

	if (hasLoadedArticle && !article) {
		content = (
			<ModalBody
				pt={ThemeSpaceVariable.XLarge}
				px={ThemeSpaceVariable.Large}
				display="flex"
				justifyContent="center"
				alignItems="center"
				flexDirection="column"
				gap={ThemeSpaceVariable.Large}
				mb={{ base: "106px", md: ThemeSpaceVariable.XLarge }}
			>
				<NotFound mt="0" showWithButton />
			</ModalBody>
		);
	}

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="md" id="article-modal">
			<ModalCloseButton isAbsolutePositioned />
			{content}
		</Modal>
	);
}

export default ArticleModal;
